/**
 * Sliding expiration lato client.
 *
 * Traccia l'ultima attività utente REALE (click, keydown, navigazione route) e,
 * tramite un timer periodico, rinnova in background l'access token quando sta per
 * scadere — ma SOLO se l'utente è stato attivo entro la finestra di inattività.
 *
 * Oltre la finestra di inattività NON si rinnova più: il token scade e il prossimo
 * 401 su una richiesta reale fa partire il logout (gestito dall'interceptor axios).
 * Questo legame attività<->rinnovo è ciò che fa effettivamente scadere la sessione.
 */
import { useAuthStore } from '../store/authStore';
import { refreshAPI } from '../api/auth';
import { router } from '../routes';
import { getTokenExp } from './jwt';

const INACTIVITY_LIMIT_MS = 50 * 60 * 1000; // 50 min: oltre questa inattività non si rinnova più
const REFRESH_THRESHOLD_MS = 180 * 1000; // rinnova se mancano <3 min alla scadenza del token
const CHECK_INTERVAL_MS = 60 * 1000; // il timer controlla ogni 60s
const ACTIVITY_THROTTLE_MS = 30 * 1000; // max una registrazione di attività ogni 30s

let lastActivity = Date.now();
let lastActivityWrite = 0;
let intervalId: number | null = null;
let unsubscribeRouter: (() => void) | null = null;
let isRefreshing = false;

/** Registra attività utente, con throttle per non sovraccaricare. */
export function markActivity(): void {
  const now = Date.now();
  if (now - lastActivityWrite < ACTIVITY_THROTTLE_MS) return;
  lastActivityWrite = now;
  lastActivity = now;
}

async function tick(): Promise<void> {
  const { isAuthenticated, token } = useAuthStore.getState();
  if (!isAuthenticated || !token) return;
  if (isRefreshing) return;

  const now = Date.now();

  // Oltre la finestra di inattività: NON rinnovare -> lascia scadere -> logout naturale al 401.
  if (now - lastActivity >= INACTIVITY_LIMIT_MS) return;

  const expMs = getTokenExp(token);
  if (expMs === null) return;

  // Rinnova solo quando il token è vicino alla scadenza (entro ~3 min).
  if (expMs - now > REFRESH_THRESHOLD_MS) return;

  isRefreshing = true;
  try {
    const newToken = await refreshAPI();
    useAuthStore.getState().setToken(newToken);
  } catch {
    // Refresh fallito (es. token già scaduto lato server): non forziamo il logout qui.
    // Il prossimo 401 su una richiesta reale lo gestirà l'interceptor axios.
  } finally {
    isRefreshing = false;
  }
}

/** Avvia il tracking attività + il timer di rinnovo. Idempotente. */
export function startSessionManager(): void {
  lastActivity = Date.now(); // l'utente ha appena fatto login / aperto l'app

  if (intervalId === null) {
    window.addEventListener('click', markActivity);
    window.addEventListener('keydown', markActivity);
    unsubscribeRouter = router.subscribe(() => markActivity());
    intervalId = window.setInterval(() => {
      void tick();
    }, CHECK_INTERVAL_MS);
  }
}

/** Ferma tracking e timer. Idempotente. Da chiamare al logout. */
export function stopSessionManager(): void {
  if (intervalId !== null) {
    window.removeEventListener('click', markActivity);
    window.removeEventListener('keydown', markActivity);
    if (unsubscribeRouter) {
      unsubscribeRouter();
      unsubscribeRouter = null;
    }
    window.clearInterval(intervalId);
    intervalId = null;
  }
}
